import { useEffect, useState } from 'react';
import type { ToolLibraryRecord } from '@bridgewerk/shared';
import { Button } from '../../../components/ui/Button.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useAssignToolToSlot } from '../../tooling/hooks/useToolLibrary.js';
import { useToolLibrary } from '../../tooling/hooks/useToolLibrary.js';

export interface AddPocketModalProps {
  readonly shopId: string;
  readonly machineId: string;
  readonly existingPockets: readonly number[];
  readonly onClose: () => void;
}

export function AddPocketModal({
  shopId,
  machineId,
  existingPockets,
  onClose,
}: AddPocketModalProps): JSX.Element {
  const toast = useToast();
  const library = useToolLibrary(shopId);
  const assign = useAssignToolToSlot(shopId);

  const nextPocket = existingPockets.length > 0
    ? Math.max(...existingPockets) + 1
    : 1;
  const [pocketNumber, setPocketNumber] = useState<number | ''>(nextPocket <= 99 ? nextPocket : '');
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pocketConflict =
    typeof pocketNumber === 'number' && existingPockets.includes(pocketNumber);

  const canSubmit =
    typeof pocketNumber === 'number' &&
    pocketNumber >= 1 &&
    pocketNumber <= 99 &&
    !pocketConflict &&
    selectedToolId !== null &&
    !assign.isPending;

  const handleAdd = async () => {
    if (!canSubmit || typeof pocketNumber !== 'number' || !selectedToolId) return;
    try {
      await assign.mutateAsync({ machineId, toolNumber: pocketNumber, toolLibraryId: selectedToolId });
      const toolName = library.data?.find((t) => t.tool_id === selectedToolId)?.description ?? 'tool';
      toast.push({
        tone: 'success',
        title: 'Pocket added',
        description: `T${String(pocketNumber).padStart(2, '0')} → ${toolName}`,
      });
      onClose();
    } catch (err) {
      toast.push({
        tone: 'critical',
        title: 'Failed to add pocket',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-pocket-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-md border border-border bg-surface shadow-xl flex flex-col max-h-[90vh]">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <h2 id="add-pocket-title" className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
              Add turret pocket
            </h2>
            <p className="mt-1 text-sm font-mono">{machineId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-dim hover:text-text leading-none px-2"
          >
            ✕
          </button>
        </header>

        {/* Pocket number picker */}
        <div className="px-4 py-3 border-b border-border shrink-0">
          <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-2">
            Pocket number
          </label>
          <div className="flex items-center gap-3">
            <span className="font-mono text-text-dim text-sm">T</span>
            <input
              type="number"
              min={1}
              max={99}
              value={pocketNumber}
              onChange={(e) => {
                const v = e.target.value;
                setPocketNumber(v === '' ? '' : Math.min(99, Math.max(1, parseInt(v, 10))));
              }}
              placeholder="01"
              className={[
                'w-20 rounded-md border bg-card px-3 py-1.5 font-mono text-sm text-center',
                'focus:outline-none focus:ring-1 focus:ring-accent',
                pocketConflict ? 'border-red text-red' : 'border-border',
              ].join(' ')}
            />
            {pocketConflict && (
              <span className="text-[11px] font-mono text-red">Pocket already exists</span>
            )}
          </div>
        </div>

        {/* Tool picker */}
        <div className="overflow-y-auto flex-1">
          <p className="px-4 pt-3 pb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">
            Select tool from library
          </p>

          {library.isLoading && (
            <div className="space-y-2 px-4 pb-4">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          )}

          {!library.isLoading && library.isError && (
            <div className="px-4 py-6 text-center text-xs font-mono text-text-dim">
              <p>Failed to load library.</p>
              <button onClick={library.refetch} className="mt-2 text-accent underline">Retry</button>
            </div>
          )}

          {!library.isLoading && !library.isError && library.data?.length === 0 && (
            <div className="px-4 py-6 text-center text-xs font-mono text-text-dim">
              <p>No tools in library yet.</p>
              <p className="mt-1 opacity-60">Add tools in the Tooling page first.</p>
            </div>
          )}

          {!library.isLoading && !library.isError && library.data && library.data.length > 0 && (
            <ul className="divide-y divide-border">
              {library.data.map((entry) => (
                <li key={entry.tool_id}>
                  <LibraryRow
                    entry={entry}
                    selected={selectedToolId === entry.tool_id}
                    onSelect={() => setSelectedToolId(entry.tool_id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border flex items-center justify-end gap-2 shrink-0">
          <Button type="button" variant="ghost" onClick={onClose} disabled={assign.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleAdd()}
            disabled={!canSubmit}
          >
            {assign.isPending ? 'Adding…' : 'Add pocket'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function LibraryRow({
  entry,
  selected,
  onSelect,
}: {
  readonly entry: ToolLibraryRecord;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'w-full text-left px-4 py-3 transition-colors border-l-2',
        selected
          ? 'bg-card border-l-accent'
          : 'border-l-transparent hover:bg-card/50',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">{entry.description}</div>
          <div className="text-[10px] font-mono text-text-dim">
            {entry.type} · {entry.material}
            {entry.diameter_mm != null && ` · ${entry.diameter_mm} mm`}
            {entry.part_number && ` · PN ${entry.part_number}`}
          </div>
        </div>
        {selected && (
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-accent shrink-0">
            Selected
          </span>
        )}
      </div>
    </button>
  );
}
